'use client';

import { ChangeEvent, useState } from 'react';
import { ReactComponent as ArrowRight } from '@assets/icons/arrow-right-light.svg';
import { ReactComponent as ChevronDown } from '@assets/icons/chevron-down.svg';
import DefaultUserAvatar from '@assets/icons/default-user-avatar.svg';
import { ReactComponent as Edit } from '@assets/icons/edit.svg';
import { ReactComponent as ImageUser } from '@assets/icons/image-user.svg';
import { logout } from 'actions/auth';
import { updateClPhoto } from 'actions/user';
import config from 'config';
import { outputSameElseDateFormat, ROUTES } from 'constant';
import { usePathname } from 'next/navigation';

import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  FormLabel,
  Input,
  Stack,
  useMediaQuery,
  useTheme,
  VStack
} from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { userSelector } from 'store/reducers/userSlice';

import { getCustomFormattedDay } from 'utils';

import { MenuItemType } from 'types';

import { Avatar } from 'components/Avatar';
import { Button, Divider, Link, Text } from 'ui';
import { AccountBalance, NextAppointment } from './components';

const {
  dashboard: {
    sidebar: { profileTabs, myAccountLinks, isReferFriend }
  }
} = config;

const smallLinkStaticStyles = {
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  padding: '10px 16px 10px 25px',

  fontSize: '14px',
  lineHeight: '120%',
  minH: '39px',
  textAlign: 'center',

  '& > svg': {
    width: '12px',
    height: '14px'
  }
};

const accordionStaticStyles = {
  width: '100%',
  border: 0,
  overflow: 'hidden',
  borderRadius: '3px'
};

const linkStaticStyles = {
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  fontSize: '14px',
  lineHeight: '120%',
  textAlign: 'center',

  '@media screen and (max-width: 767px)': {
    fontSize: '12px'
  }
};

const accordionBtnStyles = {
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '13px 12px 13px 15px',
  fontWeight: 'inherit',

  '&:hover': {
    background: 'transparent'
  }
};

const ProfileSidebar = () => {
  const dispatch = useAppDispatch();
  const {
    colors: {
      palette: {
        components: { profileSidebar }
      }
    }
  } = useTheme();
  const pathname = usePathname();
  const [isMobile] = useMediaQuery('(max-width: 850px)', {
    fallback: false
  });

  const isAccordionItem = myAccountLinks.some(({ link }: MenuItemType) =>
    pathname.includes(link)
  );
  const { user } = useAppSelector(userSelector);
  const [isExpandedAccordion, setIsExpandedAccordion] =
    useState<boolean>(isAccordionItem);

  const handleToggleAccordion = () => {
    setIsExpandedAccordion((prevState) => !prevState);
  };

  const handleCloseAccordion = () => {
    setIsExpandedAccordion(false);
  };

  const handleLogout = () => {
    dispatch(logout());
  };

  const updatePhoto = (event: ChangeEvent) => {
    const target = event.target as HTMLInputElement;
    const file: File = (target.files as FileList)[0];

    if (file) {
      dispatch(updateClPhoto(file));
    }
  };

  const accordionStyles = {
    ...accordionStaticStyles,
    backgroundColor: profileSidebar.accordionBg
  };

  const linkStyles = {
    ...linkStaticStyles,
    backgroundColor: profileSidebar.linkBg,
    color: profileSidebar.linkColor,

    '&.active-link': {
      bg: profileSidebar.linkActiveBg,
      color: profileSidebar.activeLinkColor,
      fontWeight: 'semibold',
      svg: {
        visibility: 'visible',
        fill: `${profileSidebar.activeLinkColor} !important`
      }
    }
  };

  return (
    <Stack
      w={{
        base: 'calc(100% + 40px)',
        md: isMobile ? 'calc(100% + 50px)' : '230px',
        lg: '260px',
        xl: '303px'
      }}
      flexShrink={0}
      gap={0}
      direction={isMobile ? 'row' : 'column'}
      justifyContent={isMobile ? 'space-between' : 'unset'}
      alignItems={isMobile ? 'unset' : 'center'}
      mb={0}
      mx={isMobile ? '-25px' : 0}
    >
      <Box
        display={isMobile ? 'none' : 'block'}
        maxWidth={isMobile ? 114 : 170}
        mr={isMobile ? '35px' : 0}
        textAlign='center'
      >
        <Box position='relative'>
          <Avatar
            key={`${user?.givname} ${user?.surname}`}
            src={user?.photo ?? DefaultUserAvatar}
            w={{ base: 114, md: 170 }}
            h={{ base: 114, md: 170 }}
            alt={`${user?.givname} ${user?.surname}`}
            borderRadius='50%'
            borderWidth={3}
            borderColor={profileSidebar.avatarBorderColor}
            mb='15px'
            useBadges
            isMember={!!user?.is_member}
            isVIP={user?.is_vip === 1}
            isInfluencer={user?.is_influencer === 1}
          />

          <Stack
            position='absolute'
            top='3px'
            left='3px'
            width='calc(100% - 6px)'
            height='calc(100% - 6px)'
            borderRadius='50%'
            transition='opacity .2s'
            borderWidth={3}
            borderColor='transparent'
            alignItems='center'
            justifyContent={isMobile ? 'flex-end' : 'center'}
            bg={profileSidebar.avatarHoverBg}
            opacity={0}
            _hover={{
              opacity: 1
            }}
          >
            <Stack
              pb={isMobile ? '7px' : 0}
              alignItems='center'
              justifyContent='center'
              gap='8px'
            >
              <Box display={isMobile ? 'none' : 'block'}>
                <ImageUser
                  width={30}
                  height={30}
                  fill={profileSidebar.iconFill}
                />
              </Box>
              <Box
                display={isMobile ? 'block' : 'none'}
                boxShadow='0px 3px 6px rgba(0, 0, 0, 0.16)'
              >
                <Edit width={21} height={21} fill='white' />
              </Box>
              <Text
                fontSize='12px'
                color={profileSidebar.iconText}
                textTransform='uppercase'
                fontWeight='semibold'
                display={isMobile ? 'none' : 'block'}
              >
                Update Photo
              </Text>
            </Stack>

            <Box
              as={FormLabel}
              htmlFor='avatar'
              position='absolute'
              width='100%'
              height='100%'
              left={0}
              borderRadius='50%'
              cursor='pointer'
              opacity={0}
              padding={0}
              margin={0}
            />
            <Input
              id='avatar'
              position='absolute'
              opacity={0}
              pointerEvents='none'
              type='file'
              fontSize={0}
              onChange={updatePhoto}
              accept='image/jpeg, image/png, image/jpg'
            />
          </Stack>
        </Box>

        {user?.nickname && (
          <Text
            fontWeight='semibold'
            fontSize={isMobile ? '20px' : '22px'}
            lineHeight='22px'
            color={profileSidebar.nickameColor}
          >
            {user.nickname}
          </Text>
        )}
        {user?.pronoun && (
          <Text
            fontSize={isMobile ? '11px' : '12px'}
            lineHeight='17px'
            color={profileSidebar.memberSinceTextColor}
            mt='4px'
          >
            {user.pronoun}
          </Text>
        )}
        {user?.date_start && !!user?.is_member && (
          <Text
            mt='8px'
            casing='uppercase'
            fontSize={isMobile ? '11px' : '12px'}
            lineHeight='17px'
            color={profileSidebar.memberSinceTextColor}
          >
            {`Member since ${getCustomFormattedDay(
              user.date_start,
              outputSameElseDateFormat
            )}`}
          </Text>
        )}
      </Box>

      <Box w='100%'>
        <AccountBalance isMobile={isMobile} balance={user?.balance ?? 0} />
        <NextAppointment isMobile={isMobile} />
        <Divider
          display={isMobile ? 'none' : 'block'}
          borderColor={profileSidebar.dividerColor}
          opacity={0.3}
          my='15px'
        />

        {!isMobile && (
          <VStack gap='3px' align='flex-start' width='100%'>
            {profileTabs.map(({ label, link }: MenuItemType) => (
              <Box
                key={label}
                onClick={handleCloseAccordion}
                w='100%'
                position='relative'
              >
                <Link
                  to={link}
                  sx={{
                    ...linkStyles,
                    padding: '13px 12px 13px 15px',
                    borderRadius: '3px',
                    svg: { visibility: 'hidden' }
                  }}
                >
                  <Text
                    flex='1'
                    textAlign='center'
                    textTransform='uppercase'
                    px='15px'
                  >
                    {label}
                  </Text>
                  <ArrowRight
                    style={{
                      fill: profileSidebar.linkIconColor,
                      position: 'absolute',
                      right: '15px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      opacity: 0.8
                    }}
                  />
                </Link>
              </Box>
            ))}

            <Accordion
              index={isExpandedAccordion ? [0] : [-1]}
              allowToggle
              sx={{
                ...accordionStyles,
                bg: profileSidebar.accordionBg
              }}
            >
              <AccordionItem sx={{ border: 0 }}>
                {({ isExpanded }) => (
                  <>
                    <Box
                      position='relative'
                      sx={{
                        ...linkStyles,
                        bg: isExpanded
                          ? profileSidebar.accordionOpenBg
                          : profileSidebar.accordionBg,
                        color: isExpanded
                          ? profileSidebar.accordionOpenColor
                          : profileSidebar.linkColor
                      }}
                      onClick={handleToggleAccordion}
                    >
                      <AccordionButton
                        _expanded={{ fontWeight: 600 }}
                        fontSize={isMobile ? '12px' : '14px'}
                        lineHeight={isMobile ? '17px' : '19px'}
                        sx={accordionBtnStyles}
                      >
                        <Text
                          flex='1'
                          textAlign='center'
                          textTransform='uppercase'
                          px='15px'
                        >
                          My account
                        </Text>
                        <ChevronDown
                          width={14}
                          height={14}
                          fill={
                            isExpanded
                              ? profileSidebar.accordionOpenColor
                              : profileSidebar.linkColor
                          }
                          style={{
                            position: 'absolute',
                            right: '15px',
                            top: '50%',
                            transform: isExpanded
                              ? 'translateY(-50%) scale(-1)'
                              : 'translateY(-50%) scale(1)',
                            transition: 'transform .2s',
                            opacity: 0.8
                          }}
                        />
                      </AccordionButton>
                    </Box>

                    <AccordionPanel
                      sx={{
                        padding: '0'
                      }}
                    >
                      <VStack gap={0}>
                        {myAccountLinks.map(({ label, link }: MenuItemType) => {
                          const isActive = pathname.includes(link);

                          return (
                            <Link
                              to={link}
                              key={label}
                              sx={{
                                ...smallLinkStaticStyles,
                                fontWeight: isActive ? 'semibold' : 'regular',
                                bg: isActive
                                  ? profileSidebar.smallLinkActiveBg
                                  : profileSidebar.smallLinkBg,
                                color: isActive
                                  ? profileSidebar.smallLinkActiveColor
                                  : profileSidebar.linkColor,
                                width: isActive ? 'calc(100% + 2px)' : '100%',
                                mr: isActive ? '-2px' : '0',
                                '&:last-of-type': {
                                  borderBottomRadius: '5px'
                                }
                              }}
                            >
                              <Text flex='1' textAlign='center' px='15px'>
                                {label}
                              </Text>
                              {isActive && (
                                <ArrowRight
                                  fill={
                                    profileSidebar.linkAccordionItemIconColor
                                  }
                                  style={{
                                    position: 'absolute',
                                    right: '18px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    opacity: 0.8
                                  }}
                                />
                              )}
                            </Link>
                          );
                        })}
                      </VStack>
                    </AccordionPanel>
                  </>
                )}
              </AccordionItem>
            </Accordion>

            {isReferFriend && (
              <Box onClick={handleCloseAccordion} w='100%' position='relative'>
                <Link
                  to={ROUTES.referFriend}
                  sx={{
                    ...linkStyles,
                    padding: '13px 12px 13px 15px',
                    borderRadius: '3px',
                    svg: { visibility: 'hidden' }
                  }}
                >
                  <Text
                    flex='1'
                    textAlign='center'
                    textTransform='uppercase'
                    px='15px'
                  >
                    REFER A FRIEND
                  </Text>
                  <ArrowRight
                    style={{
                      fill: profileSidebar.linkIconColor,
                      position: 'absolute',
                      right: '15px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      opacity: 0.8
                    }}
                  />
                </Link>
              </Box>
            )}
          </VStack>
        )}

        {!isMobile && (
          <>
            <Divider
              borderColor={profileSidebar.dividerColor}
              opacity={0.3}
              mt='15px'
            />
            <Button
              variant='link'
              color={profileSidebar.signOutButtonColor}
              fontSize='14px'
              lineHeight='19px'
              width='auto'
              margin='20px auto'
              justifyContent='center'
              onClick={handleLogout}
            >
              sign out
            </Button>
          </>
        )}
      </Box>
    </Stack>
  );
};

export default ProfileSidebar;
